import { createEnhancedStore } from '@tasama/utils';
import type { BaseStoreState } from '@tasama/utils';
import { userContextService } from '../services/user-context.service';

import type { UserContextResponse } from '../types/user-context.types';

/**
 * User Context Store using Zustand with store-utils
 * Manages user context data with automatic hydration from cache
 */

interface UserContextStateData {
  userContext: UserContextResponse | null;
}

interface UserContextStateActions {
  setUserContext: (_context: UserContextResponse | null) => void;
  clearUserContext: () => void;
  refreshUserContext: () => Promise<void>;
  hydrateFromCache: () => void;
}

type UserContextStoreState = BaseStoreState &
  UserContextStateData &
  UserContextStateActions;

export const useUserContextStore = createEnhancedStore<UserContextStoreState>(
  'user-context',
  {
    userContext: null,
  } as Omit<UserContextStoreState, keyof BaseStoreState>,
  (set, _get, createAsyncAction) => ({
    setUserContext: (context: UserContextResponse | null) => {
      set({
        userContext: context,
        error: null,
      } as Partial<UserContextStoreState>);
    },

    clearUserContext: () => {
      set({
        userContext: null,
        loading: false,
        error: null,
      } as Partial<UserContextStoreState>);
      userContextService.clearCache();
    },

    refreshUserContext: createAsyncAction(async () => {
      const response = await userContextService.getUserContext(true);

      if (response.status === 'success' && response.data) {
        set({ userContext: response.data } as Partial<UserContextStoreState>);
      } else {
        throw new Error(response.message || 'Failed to fetch user context');
      }
    }),

    hydrateFromCache: () => {
      const cachedData = userContextService.getCachedUserContext();
      if (cachedData) {
        set({
          userContext: cachedData,
          error: null,
        } as Partial<UserContextStoreState>);
      }
    },
  })
);

// Store hydration removed - router loader (homeLoader) is now the single source of truth
// This prevents duplicate /user/context API calls that occurred when:
// 1. Store tried to hydrate on module load
// 2. Router loader fetched on navigation
// 3. Components using useUserContextQuery triggered additional fetches

// Export store selectors for easy access
export const useUserContext = () =>
  useUserContextStore((state) => state.userContext);
export const useUserContextLoading = () =>
  useUserContextStore((state) => state.loading);
export const useUserContextError = () =>
  useUserContextStore((state) => state.error);
